import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  BadgeCheck,
  CalendarDays,
  FileText,
  Home,
  IdCard,
  Loader2,
  MapPin,
  Pencil,
  Phone,
  Check,
  Printer,
  Shirt,
  Stethoscope,
  X,
  UserRound,
  Users,
  Wallet,
} from 'lucide-react';
import {
  fetchAdherent,
  fetchConfigurationAdherents,
  updateAdherent,
  updateContactsAdherent,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { eurosDepuisCentimes as euros } from '../utils/montants';

/**
 * La fiche complète d'un adhérent, sur une page à elle.
 *
 * POURQUOI CETTE PAGE EXISTE À CÔTÉ DU VOLET
 * ---------------------------------------------------------------------------
 * L'écran du registre montre une liste à gauche et un aperçu à droite. C'est
 * la bonne forme pour PARCOURIR : on clique de nom en nom, on vérifie une
 * pièce, on passe au suivant.
 *
 * Ce n'est pas la bonne forme pour S'OCCUPER d'une personne. L'aperçu tient
 * dans un tiers d'écran, et il en montrait un tiers : état civil, contacts
 * d'urgence, pièces. Ses cotisations, ses équipements, son équipe et ses
 * convocations vivaient dans quatre autres écrans — et le bureau les
 * traversait en gardant le nom en tête.
 *
 * Cette page rassemble tout ce que le club sait d'une personne, rangé par
 * blocs, sur une page qui s'imprime.
 *
 * ELLE MODIFIE, ET SEULEMENT CE QUI LUI APPARTIENT
 * ---------------------------------------------------------------------------
 * Chaque bloc de la FICHE — identité, coordonnées, contacts, précisions de la
 * discipline, notes — porte son propre crayon : on ouvre le bloc, on corrige,
 * on enregistre. Le reste de la page ne bouge pas, et l'on n'a pas à traverser
 * un formulaire de vingt champs pour rectifier un numéro de téléphone.
 *
 * Chaque bloc s'enregistre SEUL. Un formulaire unique enverrait les vingt
 * champs à chaque correction : deux personnes qui travaillent en même temps se
 * réécriraient l'une l'autre, celle qui enregistre en dernier gagnant sans que
 * personne ne le sache.
 *
 * CE QU'ELLE NE MODIFIE PAS, ET POURQUOI
 * ---------------------------------------------------------------------------
 * Les cotisations, les pièces, les équipements, les équipes, les rencontres.
 * Chacun a son écran, et surtout ses règles : une cotisation encaissée ne se
 * réécrit pas — elle se contredit par un avoir — et une convocation appartient
 * à l'entraîneur qui compose. Les rendre modifiables ici redéfinirait chaque
 * règle une seconde fois, et les deux divergeraient.
 *
 * Ces blocs-là renvoient donc vers l'écran qui sait le faire.
 *
 * L'ORDRE DES BLOCS SUIT CELUI DES QUESTIONS
 * ---------------------------------------------------------------------------
 * Qui est-ce, comment le joindre, qui prévenir. Puis l'argent, qui est la
 * question la plus fréquente. Puis les pièces, qui bloquent. Puis le sport.
 * Les notes en dernier : on les lit quand on n'a pas trouvé la réponse ailleurs.
 */
export default function AdherentFichePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { aLeDroit, isLoading } = useAuth();

  const identifiant = Number(id);

  const fiche = useQuery({
    queryKey: ['adherent', identifiant],
    queryFn: () => fetchAdherent(identifiant),
    enabled: Number.isFinite(identifiant) && identifiant > 0,
  });

  const detail = fiche.data?.adherent ?? null;

  /*
    LES CHAMPS QUE LE CLUB A DÉCLARÉS.

    Les précisions d'une fiche ne portent que ce qui a été REMPLI. Bâtir le
    formulaire dessus ne proposerait donc jamais un champ resté vide — et sur
    une fiche neuve, aucun champ du tout : on lirait « rien de déclaré » alors
    que le club en a déclaré trois.

    La liste vient donc des réglages du registre, comme l'écran qui les crée.
  */
  const configuration = useQuery({
    queryKey: ['adherents-configuration'],
    queryFn: fetchConfigurationAdherents,
  });

  const champsDeclares: any[] = configuration.data?.configuration?.precisions ?? [];

  const peutGerer = aLeDroit('gerer_adherents');
  const client = useQueryClient();

  /*
    UN SEUL BLOC OUVERT À LA FOIS.

    Deux blocs en cours de saisie donnent deux « Enregistrer » à l'écran, et
    l'on quitte le premier sans l'avoir envoyé. Ouvrir le second referme donc
    le premier — et le referme sans enregistrer, ce que le bouton « Annuler »
    dit déjà.
  */
  const [bloc, setBloc] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; texte: string } | null>(null);

  const annoncer = (type: 'success' | 'error', texte: string) => {
    setNotice({ type, texte });
    if (type === 'success') {
      setTimeout(() => setNotice(null), 4000);
    }
  };

  const enregistrement = useMutation({
    mutationFn: (donnees: Record<string, unknown>) => updateAdherent(identifiant, donnees),
    onSuccess: (d: { success?: boolean; message?: string }) => {
      if (d?.success === false) {
        annoncer('error', d.message ?? "L'enregistrement a été refusé.");
        return;
      }
      annoncer('success', d?.message ?? 'Fiche enregistrée.');
      setBloc(null);
      void client.invalidateQueries({ queryKey: ['adherent', identifiant] });
      void client.invalidateQueries({ queryKey: ['adherents'] });
    },
    onError: (e: Error) => annoncer('error', e.message),
  });

  const enregistrementContacts = useMutation({
    mutationFn: (liste: any[]) => updateContactsAdherent(identifiant, liste),
    onSuccess: (d: { success?: boolean; message?: string }) => {
      if (d?.success === false) {
        annoncer('error', d.message ?? "L'enregistrement a été refusé.");
        return;
      }
      annoncer('success', d?.message ?? 'Contacts enregistrés.');
      setBloc(null);
      void client.invalidateQueries({ queryKey: ['adherent', identifiant] });
    },
    onError: (e: Error) => annoncer('error', e.message),
  });

  useEffect(() => {
    document.title = detail?.nomComplet ? `${detail.nomComplet} — Fiche` : 'Fiche adhérent';
  }, [detail?.nomComplet]);

  if (isLoading) {
    return null;
  }

  if (!aLeDroit('voir_adherents')) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Fiche adhérent</h1>
        <p className="text-sm text-gray-500">
          Le registre appartient au bureau et aux entraîneurs. Demandez-leur d'ouvrir ce droit si
          vous en avez l'usage.
        </p>
      </div>
    );
  }

  if (fiche.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  if (detail === null) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <UserRound className="w-10 h-10 mx-auto text-gray-300 mb-4" />
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Fiche introuvable</h1>
        <p className="text-sm text-gray-500 mb-6">
          Cet adhérent n'existe pas, ou il a été retiré du registre.
        </p>
        <Link
          to="/adherents"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
        >
          <ArrowLeft className="w-4 h-4" />
          Revenir au registre
        </Link>
      </div>
    );
  }

  const licences: any[] = detail.licences ?? [];
  const equipements: any[] = detail.equipements ?? [];
  const equipes: any[] = detail.equipes ?? [];
  const rencontres: any[] = detail.rencontres ?? [];
  const documents: any[] = detail.documents ?? [];
  const contacts: any[] = detail.contacts ?? [];
  const precisions: Record<string, unknown> = detail.precisions ?? {};

  /*
    Les contacts VIDES ne s'affichent pas.

    Le serveur en rend quatre — deux responsables, le médecin, une autre
    personne — dès qu'un seul a été touché : ce sont les quatre lignes de
    l'écran d'édition, pas quatre personnes. Les montrer toutes ferait lire
    « Médecin : — » sous chaque fiche, et l'on cesserait de regarder ce bloc.
  */
  const renseignes = contacts.filter(
    (c) => (c.nom ?? '') !== '' || (c.telephone ?? '') !== '' || (c.email ?? '') !== '',
  );

  const aVenir = rencontres.filter((r) => r.aVenir);
  const passees = [...rencontres.filter((r) => !r.aVenir)].reverse();

  /*
    Ce qu'il doit, toutes licences confondues.

    Dans une association omnisports, un adhérent peut avoir deux cotisations
    dont une seule est réglée. Un total unique en tête répond d'un coup à la
    question qu'on vient poser, et chaque licence garde ensuite son détail.
  */
  const restantDu = licences.reduce((somme, l) => somme + (l.reste ?? 0), 0);

  return (
    /*
      AUCUN PLAFOND ICI : c'est la coque qui l'accorde.

      La page portait « max-w-7xl 2xl:max-w-[100rem] » — 1280 puis 1600 px — et
      cette largeur n'a jamais servi : la coque de l'application plafonne son
      contenu à 1152 px, et une page ne peut pas élargir un conteneur qui lui
      est extérieur. Les quatre colonnes tenaient donc dans 1152 px, à deux cent
      soixante-dix pixels chacune.

      La coque ouvre maintenant la pleine largeur sur cette adresse (voir
      PLEINE_LARGEUR dans App.tsx). Le padding horizontal vient d'elle aussi sur
      grand écran ; il reste ici pour la disposition mobile, qui n'en a pas.
    */
    <div className="w-full px-4 lg:px-0 py-8">
      {/* ------------------------------ La barre ------------------------------ */}
      <div className="flex flex-wrap items-center gap-3 mb-6 print:hidden">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 min-h-[44px]"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </button>

        {/*
          Le retour au registre, et non plus « Modifier ».

          Le bouton menait à l'écran de modification parce que cette page ne
          modifiait rien. Chaque bloc porte maintenant son crayon ; le lien ne
          sert plus qu'à revenir à la liste, ce qu'il dit désormais.
        */}
        <Link
          to="/adherents"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 min-h-[44px]"
        >
          <Users className="w-4 h-4" />
          Le registre
        </Link>

        {/*
          L'impression, parce qu'une fiche se pose sur une table.

          Un dossier réclamé par une fédération, un contrôle, une réunion de
          bureau : la feuille circule là où l'écran ne va pas. Les boutons et
          la navigation disparaissent à l'impression — ils ne servent plus.
        */}
        <button
          onClick={() => window.print()}
          className="ml-auto inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 min-h-[44px]"
        >
          <Printer className="w-4 h-4" />
          Imprimer
        </button>
      </div>

      {notice !== null && (
        <p
          className={`mb-4 rounded-lg px-3 py-2 text-sm print:hidden ${
            notice.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {notice.texte}
        </p>
      )}

      {/* ------------------------------ L'en-tête ------------------------------ */}
      <header className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <div className="flex flex-wrap items-start gap-4">
          {detail.photo ? (
            <img src={detail.photo} alt="" className="w-16 h-16 rounded-full object-cover" />
          ) : (
            <span className="w-16 h-16 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xl font-semibold">
              {initiales(detail.prenom, detail.nom)}
            </span>
          )}

          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold text-gray-900">{detail.nomComplet}</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {[
                detail.categorie,
                detail.age !== null && detail.age !== undefined ? `${detail.age} ans` : null,
                detail.discipline,
              ]
                .filter(Boolean)
                .join(' · ') || 'Catégorie à préciser'}
            </p>

            <p className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <span
                className={`rounded-full px-2 py-0.5 font-medium ${
                  detail.actif
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {detail.actif ? 'au registre actif' : 'retiré du registre'}
              </span>
              {detail.aCompte === true && (
                <span className="rounded-full bg-primary-100 text-primary-800 px-2 py-0.5 font-medium">
                  a un compte
                </span>
              )}
              {restantDu > 0 && (
                <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 font-medium">
                  {euros(restantDu)} à régler
                </span>
              )}
            </p>
          </div>
        </div>

        {detail.documentsManquants?.length > 0 && (
          <p className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Pièces obligatoires manquantes ou périmées : {detail.documentsManquants.join(', ')}.
            </span>
          </p>
        )}
      </header>

      {/*
        LES BLOCS EN COLONNES, ET NON L'UN SOUS L'AUTRE.

        Empilés, les neuf blocs faisaient trois écrans de haut : on devait
        faire défiler pour savoir si une cotisation était réglée, puis remonter
        pour lire le téléphone du responsable légal. Or c'est exactement ce que
        cette page devait éviter — le bureau traversait déjà quatre écrans, on
        lui en aurait rendu un seul, mais long.

        Sur un écran d'ordinateur, tout tient d'un coup d'œil : six colonnes
        au-delà de 2200 px, quatre entre 1536 et 2200, trois entre 1280 et
        1536, deux entre 1024 et 1280, une seule sur téléphone — où
        l'empilement reste la seule forme possible, et la bonne.

        Dix blocs et deux largeurs doubles font douze cellules. Le compte tombe
        juste à toutes ces largeurs : deux lignes de six, trois de quatre,
        quatre de trois, six de deux. Ce n'est pas un hasard mais la raison
        pour laquelle les rencontres suivent les pièces.

        POURQUOI SIX ET NON CINQ
        ---------------------------------------------------------------------
        Douze ne se divise pas par cinq : la dernière ligne resterait aux deux
        tiers vide. Et pourquoi PLUS de colonnes plutôt que des colonnes plus
        larges : au-delà de 2200 px, quatre colonnes en font de 500 px chacune,
        où une liste de deux valeurs laisse un blanc au milieu. Six colonnes les
        ramènent autour de 350 px — la largeur qu'elles ont sur un écran
        ordinaire, et à laquelle elles ont été pensées.

        LES LARGEURS SUIVENT LE CONTENU
        ---------------------------------------------------------------------
        Les listes qui portent deux valeurs par ligne — les cotisations, les
        rencontres — prennent deux colonnes : serrées sur une seule, le montant
        passerait sous le libellé et la ligne se lirait en deux temps.

        Les blocs courts — identité, coordonnées, contacts — en prennent une.

        L'ORDRE, ET POURQUOI LA GRILLE COMBLE SES TROUS
        ---------------------------------------------------------------------
        Un bloc large ne descend pas dans une colonne restée seule : il attend
        la ligne suivante, et laisse un blanc derrière lui. « row-dense » y
        remonte le bloc court suivant, ce qui supprime les trous.

        Le prix est que l'ordre lu n'est plus exactement l'ordre écrit. C'est
        acceptable ici, et seulement ici : chaque bloc porte son titre et son
        icône, on ne le lit pas comme une phrase mais on le REPÈRE. Sur une
        page qui se lit de haut en bas, ce serait une faute.
      */}
      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 min-[2200px]:grid-cols-6 print:grid-cols-2 grid-flow-row-dense items-start">
        {/* ------------------------------ Identité ----------------------------- */}
        <Bloc
          titre="Identité"
          icone={IdCard}
          modifiable={peutGerer}
          ouvert={bloc === 'identite'}
          surOuvrir={() => setBloc('identite')}
          surFermer={() => setBloc(null)}
        >
          {bloc === 'identite' ? (
            <FormulaireIdentite
              detail={detail}
              enCours={enregistrement.isPending}
              onAnnuler={() => setBloc(null)}
              onValider={(donnees) => enregistrement.mutate(donnees)}
            />
          ) : (
            <dl className="grid gap-x-6 gap-y-3 grid-cols-2">
              <Ligne titre="Civilité" valeur={detail.civilite} />
              <Ligne titre="Nom de naissance" valeur={detail.nom} />
              <Ligne titre="Prénom" valeur={detail.prenom} />
              <Ligne titre="Né(e) le" valeur={jour(detail.dateNaissance)} />
              <Ligne titre="À" valeur={detail.lieuNaissance} />
              <Ligne titre="Nationalité" valeur={detail.nationalite} />
              <Ligne titre="Sexe" valeur={detail.sexe} />
              {/*
                La catégorie ne se saisit pas : elle se calcule à partir de la
                date de naissance. La rendre modifiable ici laisserait un U13
                dans la catégorie U11 après son anniversaire, sans que rien ne
                le rattrape. Le surclassement, lui, se décide sur la licence,
                se motive, et porte le nom de qui l'a décidé.
              */}
              <Ligne titre="Catégorie d'âge" valeur={detail.categorie} />
            </dl>
          )}
        </Bloc>

        {/* ---------------------------- Coordonnées ---------------------------- */}
        <Bloc
          titre="Coordonnées"
          icone={Phone}
          modifiable={peutGerer}
          ouvert={bloc === 'coordonnees'}
          surOuvrir={() => setBloc('coordonnees')}
          surFermer={() => setBloc(null)}
        >
          {bloc === 'coordonnees' ? (
            <FormulaireCoordonnees
              detail={detail}
              enCours={enregistrement.isPending}
              onAnnuler={() => setBloc(null)}
              onValider={(donnees) => enregistrement.mutate(donnees)}
            />
          ) : (
            <dl className="grid gap-x-6 gap-y-3">
              <Ligne titre="Courriel" valeur={detail.email} />
              <Ligne titre="Téléphone" valeur={detail.telephone} />
              <Ligne titre="Adresse" valeur={detail.adresse} />
              <Ligne
                titre="Commune"
                valeur={[detail.codePostal, detail.ville].filter(Boolean).join(' ') || null}
              />
            </dl>
          )}
        </Bloc>

        {/* -------------------------- Qui prévenir ----------------------------- */}
        <Bloc
          titre="Qui prévenir"
          icone={Phone}
          compte={renseignes.length}
          modifiable={peutGerer}
          ouvert={bloc === 'contacts'}
          surOuvrir={() => setBloc('contacts')}
          surFermer={() => setBloc(null)}
        >
          {bloc === 'contacts' ? (
            <FormulaireContacts
              contacts={contacts}
              enCours={enregistrementContacts.isPending}
              onAnnuler={() => setBloc(null)}
              onValider={(liste) => enregistrementContacts.mutate(liste)}
            />
          ) : renseignes.length === 0 ? (
            <Vide>
              Aucun contact renseigné. Pour un mineur, les responsables légaux sont ce qu'un
              encadrant compose sans avoir le temps de chercher.
            </Vide>
          ) : (
            <ul className="divide-y divide-gray-100">
              {renseignes.map((c) => (
                <li key={c.id ?? c.role} className="py-2.5 first:pt-0 last:pb-0">
                  <p className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                    {c.role === 'medecin' ? (
                      <Stethoscope className="w-3.5 h-3.5" />
                    ) : (
                      <UserRound className="w-3.5 h-3.5" />
                    )}
                    {LIBELLE_ROLE[c.role] ?? c.role}
                  </p>
                  <p className="mt-0.5 text-sm font-medium text-gray-900">{c.nom || '—'}</p>
                  <p className="text-sm text-gray-600">
                    {[c.telephone, c.email, c.lien].filter(Boolean).join(' · ') ||
                      'Aucune coordonnée'}
                  </p>
                  {c.remarque && <p className="mt-0.5 text-xs text-gray-500">{c.remarque}</p>}
                </li>
              ))}
            </ul>
          )}
        </Bloc>

        {/* ------------------------- Licences et argent ------------------------ */}
        <Bloc titre="Licences et cotisations" icone={Wallet} compte={licences.length} large>
          {licences.length === 0 ? (
            <Vide>Aucune licence. Inscrivez-le depuis le registre.</Vide>
          ) : (
            <ul className="divide-y divide-gray-100">
              {licences.map((l) => (
                <li key={l.id} className="py-3 first:pt-0 last:pb-0">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                    <p className="text-sm font-medium text-gray-900">
                      {l.saison}
                      {l.discipline && <span className="text-gray-600"> · {l.discipline}</span>}
                      {l.categorie && <span className="text-gray-500"> · {l.categorie}</span>}
                    </p>
                    <p className="text-sm tabular-nums">
                      <span className="text-gray-500">{euros(l.montant)}</span>
                      {l.reste > 0 ? (
                        <span className={l.enRetard ? 'ml-2 text-red-700' : 'ml-2 text-amber-700'}>
                          {euros(l.reste)} dus
                        </span>
                      ) : (
                        <span className="ml-2 text-green-700">soldée</span>
                      )}
                    </p>
                  </div>

                  <p className="mt-0.5 text-xs text-gray-500">
                    {[
                      l.numero ? `n° ${l.numero}` : null,
                      l.type,
                      l.statutPaiement,
                      l.echeance ? `échéance ${jour(l.echeance)}` : null,
                      l.enRetard ? 'en retard' : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Bloc>

        {/* ------------------------------ Les pièces --------------------------- */}
        <Bloc titre="Pièces au dossier" icone={FileText} compte={documents.length}>
          {documents.length === 0 ? (
            <Vide>Aucune pièce déposée.</Vide>
          ) : (
            <ul className="divide-y divide-gray-100">
              {documents.map((d) => (
                <li key={d.id} className="flex flex-wrap items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 min-w-0 truncate text-sm text-gray-800 hover:text-primary-700"
                  >
                    {d.libelle}
                  </a>
                  <span className="text-xs text-gray-500">{jour(d.deposeLe)}</span>
                  {d.valableJusquau && (
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${
                        d.perime ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {d.perime ? 'périmé' : `jusqu'au ${jour(d.valableJusquau)}`}
                    </span>
                  )}
                  {d.deposeParAdherent === true && (
                    <span className="rounded-full bg-primary-50 text-primary-700 px-2 py-0.5 text-xs">
                      déposé par la famille
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Bloc>

        {/* ----------------------------- Rencontres ---------------------------- */}
        <Bloc titre="Rencontres" icone={CalendarDays} compte={rencontres.length} large>
          {rencontres.length === 0 ? (
            <Vide>
              Aucune convocation.{' '}
              <Link to="/adherents/rencontres" className="text-primary-700 hover:underline">
                Ouvrir le calendrier
              </Link>
              .
            </Vide>
          ) : (
            <>
              {aVenir.length > 0 && (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                    À venir
                  </p>
                  <ul className="divide-y divide-gray-100 mb-3">
                    {aVenir.map((r) => (
                      <LigneRencontre key={r.id} rencontre={r} />
                    ))}
                  </ul>
                </>
              )}

              {passees.length > 0 && (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                    Déjà jouées
                  </p>
                  <ul className="divide-y divide-gray-100">
                    {passees.map((r) => (
                      <LigneRencontre key={r.id} rencontre={r} />
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </Bloc>

        {/* ------------------------------ Équipements -------------------------- */}
        <Bloc titre="Équipements remis" icone={Shirt} compte={equipements.length}>
          {equipements.length === 0 ? (
            <Vide>Rien ne lui a été remis.</Vide>
          ) : (
            <ul className="divide-y divide-gray-100">
              {equipements.map((e) => (
                <li
                  key={e.id}
                  className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2.5 first:pt-0 last:pb-0"
                >
                  <span className="text-sm text-gray-800">
                    {e.libelle}
                    {e.taille && <span className="text-gray-500"> · taille {e.taille}</span>}
                    {e.numero && <span className="text-gray-500"> · n° {e.numero}</span>}
                    {e.quantite > 1 && <span className="text-gray-500"> · ×{e.quantite}</span>}
                  </span>
                  <span
                    className={`text-xs ${
                      e.enRetard ? 'text-red-700' : e.attendu ? 'text-amber-700' : 'text-gray-500'
                    }`}
                  >
                    {e.statutLibelle}
                    {e.rendreAvant && e.renduLe === null && ` · à rendre avant le ${jour(e.rendreAvant)}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Bloc>

        {/* ------------------------------- Équipes ----------------------------- */}
        <Bloc titre="Équipes" icone={Users} compte={equipes.length}>
          {equipes.length === 0 ? (
            <Vide>
              Aucune équipe. L'entraîneur compose les groupes en début de saison.
            </Vide>
          ) : (
            <ul className="divide-y divide-gray-100">
              {equipes.map((e) => (
                <li
                  key={e.id}
                  className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2.5 first:pt-0 last:pb-0"
                >
                  {/*
                    Le nom de l'équipe mène à sa composition.

                    C'est le geste qu'on faisait en repassant par le menu :
                    « il est en U15, voyons qui d'autre y est ». Trois clics
                    pour une question qui en vaut un.
                  */}
                  <span className="text-sm text-gray-800">
                    <Link to="/adherents/equipes" className="hover:text-primary-700">
                      {e.nom}
                    </Link>
                    {e.numero && <span className="text-gray-500"> · n° {e.numero}</span>}
                    {e.role && <span className="text-gray-500"> · {e.role}</span>}
                  </span>
                  <span className="text-xs text-gray-500">
                    {[e.saison, e.categorie, e.engagement].filter(Boolean).join(' · ')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Bloc>

        {/* ------------------- Ce que la discipline demande -------------------- */}
        {/*
          Le bloc s'affiche même vide dès qu'on peut le remplir.

          Il ne paraissait qu'une fois une précision saisie — donc jamais, pour
          un club qui n'en avait encore posé aucune : il fallait deviner que
          cela existait. Avec le crayon, on peut en ajouter une, et le bloc doit
          donc être là pour qu'on la trouve.
        */}
        {(Object.keys(precisions).length > 0 || peutGerer) && (
          <Bloc
            titre="Précisions propres à la discipline"
            icone={BadgeCheck}
            modifiable={peutGerer}
            ouvert={bloc === 'precisions'}
            surOuvrir={() => setBloc('precisions')}
            surFermer={() => setBloc(null)}
          >
            {bloc === 'precisions' ? (
              <FormulairePrecisions
                precisions={precisions}
                declares={champsDeclares}
                enCours={enregistrement.isPending}
                onAnnuler={() => setBloc(null)}
                onValider={(valeurs) => enregistrement.mutate({ precisions: valeurs })}
              />
            ) : Object.keys(precisions).length === 0 ? (
              <Vide>
                Rien de particulier. Ce sont les renseignements que votre discipline demande et
                qu'aucune autre ne demande : un poste, un grade, un niveau.
              </Vide>
            ) : (
              <dl className="grid gap-x-6 gap-y-3">
                {Object.entries(precisions).map(([cle, valeur]) => (
                  <Ligne
                    key={cle}
                    /*
                      Le LIBELLÉ du club, et non son code technique. « poste »
                      est ce que la base retient ; « Poste » est ce que le club
                      a écrit, et c'est ce qu'il doit relire.
                    */
                    titre={champsDeclares.find((c) => c.code === cle)?.libelle ?? cle}
                    valeur={String(valeur ?? '')}
                  />
                ))}
              </dl>
            )}
          </Bloc>
        )}

        {/* ------------------------------- Notes ------------------------------- */}
        {(detail.commentaire || peutGerer) && (
          <Bloc
            titre="Notes du bureau"
            icone={FileText}
            modifiable={peutGerer}
            ouvert={bloc === 'notes'}
            surOuvrir={() => setBloc('notes')}
            surFermer={() => setBloc(null)}
          >
            {bloc === 'notes' ? (
              <FormulaireNotes
                valeur={detail.commentaire ?? ''}
                enCours={enregistrement.isPending}
                onAnnuler={() => setBloc(null)}
                onValider={(commentaire) => enregistrement.mutate({ commentaire })}
              />
            ) : detail.commentaire ? (
              <p className="whitespace-pre-wrap text-sm text-gray-700">{detail.commentaire}</p>
            ) : (
              <Vide>
                Rien de noté. Ce qu'on écrit ici n'est vu que du bureau — jamais de la famille.
              </Vide>
            )}
          </Bloc>
        )}

      </div>

      {/*
        Les dates de la fiche, en pied et en petit.

        Elles ne servent qu'une fois par an, quand on se demande depuis quand
        quelqu'un est au club ou qui a touché à sa fiche en dernier. Les mettre
        en tête ferait lire une date de création avant un nom.

        Hors de la grille : une date de création n'est pas un bloc, et lui
        donner une colonne laisserait un trou à côté.
      */}
      <p className="mt-4 text-xs text-gray-400 text-center">
        Fiche créée le {jour(detail.createdAt)}
        {detail.updatedAt && ` · modifiée le ${jour(detail.updatedAt)}`}
      </p>
    </div>
  );
}

/* ------------------------------- Les pièces ------------------------------ */

/** Les mêmes libellés que l'écran d'édition : deux mots pour la même chose sèment le doute. */
const LIBELLE_ROLE: Record<string, string> = {
  parent1: 'Responsable légal 1',
  parent2: 'Responsable légal 2',
  medecin: 'Médecin',
  autre: 'Autre personne',
};

const PLACE: Record<string, string> = {
  convoque: 'convoqué',
  present: 'présent',
  absent: 'absent',
  excuse: 'excusé',
};

const ISSUE: Record<string, { texte: string; classe: string }> = {
  gagnee: { texte: 'gagné', classe: 'text-green-700' },
  nulle: { texte: 'nul', classe: 'text-gray-600' },
  perdue: { texte: 'perdu', classe: 'text-red-700' },
};

function LigneRencontre({ rencontre }: { rencontre: any }) {
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2.5">
      <span className="text-sm text-gray-800">
        <span className="inline-flex items-center gap-1 text-gray-500">
          {rencontre.aDomicile ? (
            <Home className="w-3.5 h-3.5" />
          ) : (
            <MapPin className="w-3.5 h-3.5" />
          )}
        </span>{' '}
        {rencontre.aDomicile ? 'Contre' : 'Chez'} {rencontre.adversaire}
        {rencontre.saPlace && (
          <span className="ml-2 text-xs text-gray-500">
            {PLACE[rencontre.saPlace] ?? rencontre.saPlace}
            {rencontre.sonRole && ` · ${rencontre.sonRole}`}
          </span>
        )}
      </span>

      <span className="flex items-center gap-2 text-xs">
        <span className="text-gray-500 first-letter:uppercase">{quand(rencontre.debut)}</span>
        {rencontre.score && (
          <span className="tabular-nums text-gray-800">{rencontre.score}</span>
        )}
        {rencontre.issue && (
          <span className={ISSUE[rencontre.issue]?.classe ?? 'text-gray-500'}>
            {ISSUE[rencontre.issue]?.texte ?? rencontre.issue}
          </span>
        )}
        {(rencontre.statut === 'annulee' || rencontre.statut === 'reportee') && (
          <span className="text-amber-700">
            {rencontre.statut === 'annulee' ? 'annulée' : 'reportée'}
          </span>
        )}
      </span>
    </li>
  );
}

function Bloc({
  titre,
  icone: Icone,
  compte,
  large = false,
  modifiable = false,
  ouvert = false,
  surOuvrir,
  surFermer,
  children,
}: {
  titre: string;
  icone: React.ComponentType<{ className?: string }>;
  compte?: number;
  /**
   * Le bloc prend deux colonnes au lieu d'une.
   *
   * Réservé aux listes qui portent deux valeurs par ligne — un libellé à
   * gauche, un montant ou une date à droite. Serrées sur une colonne, les deux
   * passent l'une sous l'autre et la ligne se lit en deux temps.
   */
  large?: boolean;
  /**
   * Le bloc porte un crayon.
   *
   * Faux pour ceux qui ont leur propre écran et leurs propres règles — les
   * cotisations, les pièces, les rencontres. Faux aussi pour qui n'a que le
   * droit de voir : le crayon n'apparaît pas plutôt que de mener à un refus.
   */
  modifiable?: boolean;
  ouvert?: boolean;
  surOuvrir?: () => void;
  surFermer?: () => void;
  children: React.ReactNode;
}) {
  return (
    <section
      className={`bg-white rounded-xl border p-5 break-inside-avoid ${
        ouvert ? 'border-primary-300 ring-1 ring-primary-100' : 'border-gray-200'
      } ${large ? 'lg:col-span-2 print:col-span-2' : ''}`}
    >
      <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3">
        <Icone className="w-4 h-4 text-primary-600" />
        {titre}
        {compte !== undefined && compte > 0 && (
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-normal text-gray-600">
            {compte}
          </span>
        )}

        {/*
          Le crayon en bout de titre, et il disparaît à l'impression.

          Une fiche imprimée ne se modifie pas ; garder l'icône y laisserait un
          bouton qui ne fait rien, ce qui est la définition d'un bouton mort.
        */}
        {modifiable && !ouvert && (
          <button
            type="button"
            onClick={surOuvrir}
            title={`Modifier : ${titre}`}
            aria-label={`Modifier : ${titre}`}
            className="ml-auto p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 print:hidden"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}

        {modifiable && ouvert && (
          <button
            type="button"
            onClick={surFermer}
            title="Fermer sans enregistrer"
            aria-label="Fermer sans enregistrer"
            className="ml-auto p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 print:hidden"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </h2>
      {children}
    </section>
  );
}

/* --------------------------- Les pièces d'un formulaire ------------------ */

/**
 * Un champ, avec son libellé au-dessus.
 *
 * La même forme que « Ligne » en lecture : le titre en petites capitales, la
 * valeur dessous. L'œil retrouve les champs au même endroit selon qu'il lit ou
 * qu'il corrige, ce qui évite de rechercher la ligne qu'on venait modifier.
 */
function Champ({
  titre,
  valeur,
  onChange,
  type = 'text',
  large = false,
}: {
  titre: string;
  valeur: string;
  onChange: (valeur: string) => void;
  type?: string;
  large?: boolean;
}) {
  return (
    <label className={large ? 'sm:col-span-2' : ''}>
      <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
        {titre}
      </span>
      <input
        type={type}
        value={valeur}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800"
      />
    </label>
  );
}

/** Les deux boutons de fin de formulaire, écrits une fois. */
function Boutons({
  enCours,
  onAnnuler,
  onValider,
}: {
  enCours: boolean;
  onAnnuler: () => void;
  onValider: () => void;
}) {
  return (
    <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
      <button
        type="button"
        onClick={onAnnuler}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100 min-h-[44px]"
      >
        <X className="w-4 h-4" />
        Annuler
      </button>
      <button
        type="button"
        onClick={onValider}
        disabled={enCours}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60 min-h-[44px]"
      >
        {enCours ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        Enregistrer
      </button>
    </div>
  );
}

function FormulaireIdentite({
  detail,
  enCours,
  onAnnuler,
  onValider,
}: {
  detail: any;
  enCours: boolean;
  onAnnuler: () => void;
  onValider: (donnees: Record<string, unknown>) => void;
}) {
  const [form, setForm] = useState({
    civilite: detail.civilite ?? '',
    prenom: detail.prenom ?? '',
    nom: detail.nom ?? '',
    dateNaissance: detail.dateNaissance ?? '',
    lieuNaissance: detail.lieuNaissance ?? '',
    nationalite: detail.nationalite ?? '',
    sexe: detail.sexe ?? '',
  });

  const modifier = (patch: Partial<typeof form>) => setForm({ ...form, ...patch });

  return (
    <div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Champ titre="Civilité" valeur={form.civilite} onChange={(v) => modifier({ civilite: v })} />
        <Champ titre="Sexe" valeur={form.sexe} onChange={(v) => modifier({ sexe: v })} />
        <Champ titre="Prénom" valeur={form.prenom} onChange={(v) => modifier({ prenom: v })} />
        <Champ titre="Nom" valeur={form.nom} onChange={(v) => modifier({ nom: v })} />
        <Champ
          titre="Né(e) le"
          type="date"
          valeur={form.dateNaissance}
          onChange={(v) => modifier({ dateNaissance: v })}
        />
        <Champ
          titre="À"
          valeur={form.lieuNaissance}
          onChange={(v) => modifier({ lieuNaissance: v })}
        />
        <Champ
          titre="Nationalité"
          valeur={form.nationalite}
          onChange={(v) => modifier({ nationalite: v })}
        />
      </div>

      {/*
        La date de naissance décide de la catégorie d'âge, et le dire ici évite
        de chercher ailleurs pourquoi un enfant a changé de groupe.
      */}
      <p className="mt-2 text-xs text-gray-500">
        La catégorie d'âge se recalcule seule à partir de la date de naissance.
      </p>

      <Boutons enCours={enCours} onAnnuler={onAnnuler} onValider={() => onValider(form)} />
    </div>
  );
}

function FormulaireCoordonnees({
  detail,
  enCours,
  onAnnuler,
  onValider,
}: {
  detail: any;
  enCours: boolean;
  onAnnuler: () => void;
  onValider: (donnees: Record<string, unknown>) => void;
}) {
  const [form, setForm] = useState({
    email: detail.email ?? '',
    telephone: detail.telephone ?? '',
    adresse: detail.adresse ?? '',
    codePostal: detail.codePostal ?? '',
    ville: detail.ville ?? '',
  });

  const modifier = (patch: Partial<typeof form>) => setForm({ ...form, ...patch });

  return (
    <div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Champ
          titre="Courriel"
          type="email"
          valeur={form.email}
          onChange={(v) => modifier({ email: v })}
          large
        />
        <Champ
          titre="Téléphone"
          valeur={form.telephone}
          onChange={(v) => modifier({ telephone: v })}
          large
        />
        <Champ
          titre="Adresse"
          valeur={form.adresse}
          onChange={(v) => modifier({ adresse: v })}
          large
        />
        <Champ
          titre="Code postal"
          valeur={form.codePostal}
          onChange={(v) => modifier({ codePostal: v })}
        />
        <Champ titre="Commune" valeur={form.ville} onChange={(v) => modifier({ ville: v })} />
      </div>

      <Boutons enCours={enCours} onAnnuler={onAnnuler} onValider={() => onValider(form)} />
    </div>
  );
}

/** Les quatre contacts que le club doit pouvoir appeler un soir de match. */
const CONTACTS_MODELE = [
  { role: 'parent1', nom: '', lien: '', telephone: '', email: '', remarque: '' },
  { role: 'parent2', nom: '', lien: '', telephone: '', email: '', remarque: '' },
  { role: 'medecin', nom: '', lien: '', telephone: '', email: '', remarque: '' },
  { role: 'autre', nom: '', lien: '', telephone: '', email: '', remarque: '' },
];

/**
 * Les contacts s'enregistrent EN BLOC.
 *
 * La liste est courte et fixe — quatre rôles — et un envoi complet évite les
 * demi-listes quand deux personnes la touchent en même temps. C'est la règle
 * que suit déjà l'écran du registre ; en poser une autre ici ferait deux
 * comportements pour le même geste.
 *
 * Les quatre lignes s'affichent même vides : on ne peut pas remplir un
 * responsable légal 2 dont le champ n'existe pas à l'écran.
 */
function FormulaireContacts({
  contacts,
  enCours,
  onAnnuler,
  onValider,
}: {
  contacts: any[];
  enCours: boolean;
  onAnnuler: () => void;
  onValider: (liste: any[]) => void;
}) {
  const depart = useMemo(
    () =>
      CONTACTS_MODELE.map((modele) => {
        const trouve = contacts.find((c) => c.role === modele.role);
        return trouve ? { ...modele, ...trouve } : { ...modele };
      }),
    [contacts],
  );

  const [liste, setListe] = useState(depart);

  const modifier = (index: number, patch: Record<string, string>) =>
    setListe(liste.map((c, i) => (i === index ? { ...c, ...patch } : c)));

  return (
    <div className="space-y-3">
      {liste.map((contact, index) => (
        <div key={contact.role} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-gray-600">
            {contact.role === 'medecin' ? (
              <Stethoscope className="w-3.5 h-3.5" />
            ) : (
              <UserRound className="w-3.5 h-3.5" />
            )}
            {LIBELLE_ROLE[contact.role] ?? contact.role}
          </p>

          <div className="grid gap-2 sm:grid-cols-2">
            <input
              value={contact.nom ?? ''}
              onChange={(e) => modifier(index, { nom: e.target.value })}
              placeholder="Nom"
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              value={contact.telephone ?? ''}
              onChange={(e) => modifier(index, { telephone: e.target.value })}
              placeholder="Téléphone"
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              value={contact.lien ?? ''}
              onChange={(e) => modifier(index, { lien: e.target.value })}
              placeholder={contact.role === 'medecin' ? 'Cabinet' : 'Lien de parenté'}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              value={contact.email ?? ''}
              onChange={(e) => modifier(index, { email: e.target.value })}
              placeholder="Courriel"
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        </div>
      ))}

      <Boutons enCours={enCours} onAnnuler={onAnnuler} onValider={() => onValider(liste)} />
    </div>
  );
}

/**
 * Les précisions propres à la discipline.
 *
 * POURQUOI ON N'AJOUTE PAS DE NOUVEAU NOM ICI
 * ---------------------------------------------------------------------------
 * Ces champs sont DÉCLARÉS par le club, dans les réglages du registre :
 * « Champs de la fiche ». Les inventer au fil des fiches donnerait un club où
 * la moitié des licenciés ont un « poste » et l'autre un « Poste », et aucune
 * liste ne les retrouverait ensemble.
 *
 * On corrige donc les valeurs de ce qui est déjà là, et le texte renvoie à
 * l'écran qui déclare.
 */
function FormulairePrecisions({
  precisions,
  declares,
  enCours,
  onAnnuler,
  onValider,
}: {
  precisions: Record<string, unknown>;
  /** Les champs que le club a déclarés dans les réglages du registre. */
  declares: any[];
  enCours: boolean;
  onAnnuler: () => void;
  onValider: (valeurs: Record<string, string>) => void;
}) {
  /*
    LES CHAMPS DÉCLARÉS D'ABORD, PUIS CE QUI TRAÎNE.

    Un club qui retire un champ de ses réglages laisse la valeur sur les fiches
    déjà remplies : elle n'est plus déclarée, mais elle existe. La cacher
    reviendrait à l'effacer du point de vue de qui regarde, sans l'effacer
    vraiment — et elle ressortirait un jour dans un export.

    On montre donc les deux, les déclarés en tête.
  */
  const lignes = useMemo(() => {
    const codesDeclares = declares.map((c) => String(c.code));

    const orphelins = Object.keys(precisions)
      .filter((cle) => !codesDeclares.includes(cle))
      .map((cle) => ({ code: cle, libelle: cle, type: 'texte', valeurs: [], orphelin: true }));

    return [
      ...declares.map((c) => ({ ...c, code: String(c.code), orphelin: false })),
      ...orphelins,
    ];
  }, [declares, precisions]);

  const [valeurs, setValeurs] = useState<Record<string, string>>(() =>
    Object.fromEntries(Object.entries(precisions).map(([c, v]) => [c, String(v ?? '')])),
  );

  if (lignes.length === 0) {
    return (
      <div>
        <p className="text-sm text-gray-600">
          Aucun champ n'est déclaré pour votre discipline. Vous les créez dans{' '}
          <strong>Réglages du registre → Adapter à votre club → Champs de la fiche</strong> ; ils
          apparaîtront ensuite ici, sur toutes les fiches.
        </p>
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onAnnuler}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100 min-h-[44px]"
          >
            <X className="w-4 h-4" />
            Fermer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="grid gap-3">
        {lignes.map((champ) => (
          <label key={champ.code}>
            <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
              {champ.libelle || champ.code}
              {champ.orphelin === true && (
                <span className="ml-1 font-normal normal-case text-amber-700">
                  — champ retiré des réglages
                </span>
              )}
            </span>

            {/*
              Une liste se choisit, un texte se tape.

              Un club qui a déclaré « Niveau : débutant, confirmé, compétiteur »
              l'a fait pour que les trois mots soient les mêmes sur toutes les
              fiches. Un champ libre y rendrait « Confirmé », « confirme » et
              « Confirmé(e) », qu'aucun filtre ne retrouverait ensemble.
            */}
            {champ.type === 'liste' && Array.isArray(champ.valeurs) && champ.valeurs.length > 0 ? (
              <select
                value={valeurs[champ.code] ?? ''}
                onChange={(e) => setValeurs({ ...valeurs, [champ.code]: e.target.value })}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800"
              >
                <option value="">—</option>
                {champ.valeurs.map((v: string) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={valeurs[champ.code] ?? ''}
                onChange={(e) => setValeurs({ ...valeurs, [champ.code]: e.target.value })}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800"
              />
            )}
          </label>
        ))}
      </div>

      <p className="mt-2 text-xs text-gray-500">
        Vider un champ le retire de cette fiche. Les champs eux-mêmes se déclarent dans les réglages
        du registre, et valent alors pour tout le club.
      </p>

      <Boutons enCours={enCours} onAnnuler={onAnnuler} onValider={() => onValider(valeurs)} />
    </div>
  );
}

function FormulaireNotes({
  valeur,
  enCours,
  onAnnuler,
  onValider,
}: {
  valeur: string;
  enCours: boolean;
  onAnnuler: () => void;
  onValider: (commentaire: string) => void;
}) {
  const [texte, setTexte] = useState(valeur);

  return (
    <div>
      <textarea
        value={texte}
        onChange={(e) => setTexte(e.target.value)}
        rows={6}
        placeholder="Ce qu'il faut se rappeler à propos de cette personne."
        className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800"
      />
      <p className="mt-1 text-xs text-gray-500">
        Vu du bureau seulement. La famille n'y a jamais accès depuis son espace.
      </p>

      <Boutons enCours={enCours} onAnnuler={onAnnuler} onValider={() => onValider(texte)} />
    </div>
  );
}

function Ligne({ titre, valeur }: { titre: string; valeur?: string | null }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{titre}</dt>
      <dd className="text-sm text-gray-800">
        {valeur !== null && valeur !== undefined && valeur !== '' ? (
          valeur
        ) : (
          <span className="text-gray-400">non renseigné</span>
        )}
      </dd>
    </div>
  );
}

/**
 * Ce qu'on écrit quand un bloc est vide.
 *
 * Une phrase qui dit ce qu'il faudrait faire, et non « aucune donnée ». Un
 * bloc vide sans explication laisse croire à un écran cassé ; le même bloc
 * avec sa phrase dit qu'il n'y a rien À VOIR, ce qui n'est pas la même chose.
 */
function Vide({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-gray-500">{children}</p>;
}

function initiales(prenom?: string | null, nom?: string | null): string {
  return `${(prenom ?? '').charAt(0)}${(nom ?? '').charAt(0)}`.toUpperCase() || '?';
}

function jour(iso?: string | null): string | null {
  if (iso === null || iso === undefined || iso === '') {
    return null;
  }

  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('fr-FR');
}

function quand(iso?: string | null): string {
  if (iso === null || iso === undefined) {
    return '';
  }

  return new Date(iso).toLocaleString('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
